import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Alert } from "PFComponents/alert";
import { Stack } from "PFComponents/containers";
import { Modal } from "PFComponents/modal";
import { Typography } from "PFComponents/typography";
import { ListItem, UnorderedList } from "PFComponents/ul";
import { useBookings } from "PFCore/hooks/queries";
import { useResetEngagement } from "PFCore/hooks/queries/audit_planner/audit_engagements/use_reset_engagement";
import { useAuditRolesByEngagementsQuery } from "PFCore/hooks/queries/audit_planner/audit_roles/use_audit_roles_by_engagements_query";
import { Activity } from "PFTypes";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

export type ResetEngagementModalProps = {
  activity: Activity;
  onClose: () => void;
  onSuccess?: () => void;
};

export const ResetEngagementModal = ({ activity, onClose, onSuccess }: ResetEngagementModalProps) => {
  const { t } = useTranslation("activities");
  const { t: tCore } = useTranslation("core");
  const { showGrowl } = useGrowlStoreActions();
  const navigate = useNavigate();

  const { data: engagementBookings, isLoading: isLoadingEngagementBookings } = useBookings({
    perPage: 0,
    activityId: activity.id
  });

  const { rolesByEngagement, isLoadingByEngagement } = useAuditRolesByEngagementsQuery({
    auditEngagementData: [{ auditEngagementId: activity.id, enabled: true, page: 1 }]
  });

  const auditRoleIds =
    (rolesByEngagement && rolesByEngagement[activity.id]?.entries.map(({ activity }) => activity.id)) || [];

  const { data: auditRoleBookings, isLoading: isLoadingAuditRoleBookings } = useBookings(
    {
      activityId: auditRoleIds,
      perPage: 0
    },
    {
      enabled: !isLoadingByEngagement[activity.id]
    }
  );

  const isLoadingBookings = isLoadingEngagementBookings || isLoadingAuditRoleBookings;

  const auditRoleBookingsCount = auditRoleBookings?.meta.total || 0;
  const engagementBookingsCount = engagementBookings?.meta.total || 0;

  const totalBookingsCount = engagementBookingsCount + auditRoleBookingsCount;

  const { mutateAsync: resetEngagement, isPending } = useResetEngagement();

  const handleReset = async () => {
    try {
      await resetEngagement(activity.id);
      showGrowl({ message: t("resetActivity.success"), kind: "success" });
      onSuccess?.();
      onClose();
      navigate("/audit_planner");
    } catch {
      showGrowl({ message: t("resetActivity.fail"), kind: "error" });
    }
  };

  return (
    <Modal
      title={t("resetActivity.title")}
      onOK={handleReset}
      onClose={onClose}
      labelOK={tCore("activityActions.reset")}
      kindOK="danger"
      disableOKButton={isPending}
      footerLabel={t("resetActivity.footerWarning")}
    >
      <Stack gap="spacingLg">
        {!isLoadingBookings && totalBookingsCount > 0 && (
          <Alert
            kind="warning"
            title={
              <Trans
                t={t}
                i18nKey={"resetActivity.alert"}
                values={{
                  count: totalBookingsCount
                }}
                components={[<strong key="0" />]}
              />
            }
          />
        )}
        <Typography variant="bodyRegular">
          {t("resetActivity.confirmation", { activityName: activity.name })}
        </Typography>
        <UnorderedList>
          <ListItem>{t("resetActivity.consequences.deleteBookings")}</ListItem>
          <ListItem>{t("resetActivity.consequences.removeShortlists")}</ListItem>
          <ListItem>{t("resetActivity.consequences.reopenVacancies")}</ListItem>
          <ListItem>{t("resetActivity.consequences.resetWorkflow")}</ListItem>
          <ListItem>{t("resetActivity.consequences.desynchronizeHal")}</ListItem>
          <ListItem>{t("resetActivity.consequences.cleanupSearches")}</ListItem>
        </UnorderedList>
      </Stack>
    </Modal>
  );
};
